import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { colors, typography, radius, spacing } from '../../../theme';
import { getAllUserPRs, recentOf, getPRProgression } from '../../../services/prService';
import LoadFailed from '../LoadFailed';
import { getAllEstimated1RMs, getStrengthIncrease } from '../../../services/oneRmService';
import RecordSummary from './RecordSummary';
import LatestPR from './LatestPR';
import PersonalBests from './PersonalBests';
import PersonalBestsFull from './PersonalBestsFull';

/**
 * Records tab — "What personal bests have I achieved?"
 * All figures sourced from prService.js/oneRmService.js (the correct,
 * rep-range-bucketed PR detection), not EnhancedStats.jsx's old inline
 * volume-ranked duplicate.
 */
const RecordsTab = ({ userId }) => {
  const [prs, setPrs] = useState([]);
  const [recentCount, setRecentCount] = useState(0);
  const [latestPR, setLatestPR] = useState(null);
  const [showAllPRs, setShowAllPRs] = useState(false);
  const [progressionFor, setProgressionFor] = useState(null);
  const [progressionData, setProgressionData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [progressionFailed, setProgressionFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    (async () => {
      setLoadFailed(false);
      let allPRs;
      try {
        allPRs = await getAllUserPRs(userId, { strict: true });
      } catch {
        if (!cancelled) setLoadFailed(true);
        return;
      }
      if (cancelled) return;
      setPrs(allPRs);
      setRecentCount(recentOf(allPRs, 30).length);

      const latest = allPRs[0];
      if (!latest) return;

      // Enrich the latest PR with estimated-1RM + delta, cross-referenced
      // from oneRmService.js — reused, not re-implemented.
      const [estimatedList, increase] = await Promise.all([
        getAllEstimated1RMs(userId),
        getStrengthIncrease(userId, latest.exercise_name, 3),
      ]);
      if (cancelled) return;

      const estimated = estimatedList.find((e) => e.exerciseName === latest.exercise_name);
      setLatestPR({
        ...latest,
        estimated1RM: estimated?.estimated1RM,
        increaseKg: increase?.increaseKg,
        increasePercent: increase?.increasePercent,
      });
    })();

    return () => { cancelled = true; };
  }, [userId, attempt]);

  const handleSelectExercise = async (pr) => {
    setProgressionFor(pr);
    setProgressionData(null);
    setProgressionFailed(false);
    try {
      setProgressionData(await getPRProgression(userId, pr.exercise_name, pr.rep_range, pr.is_assisted, { strict: true }));
    } catch {
      setProgressionFailed(true);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ ...typography.screenTitle, color: colors.text }}>Records</div>
        <div style={{ ...typography.body, color: colors.textMuted }}>Your personal bests</div>
      </div>

      {loadFailed && <LoadFailed what="your records" onRetry={() => setAttempt((n) => n + 1)} />}
      <RecordSummary totalPRs={prs.length} recentCount={recentCount} />
      <LatestPR latestPR={latestPR} />
      <PersonalBests prs={prs} onViewAll={() => setShowAllPRs(true)} />

      {showAllPRs && (
        <PersonalBestsFull
          prs={prs}
          onClose={() => setShowAllPRs(false)}
          onSelectExercise={handleSelectExercise}
        />
      )}

      {progressionFor && (
        <div style={styles.overlay} onClick={() => setProgressionFor(null)}>
          <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={styles.header}>
              <div style={{ ...typography.sectionHeading, color: colors.text }}>{progressionFor.exercise_name}</div>
              <button onClick={() => setProgressionFor(null)} style={styles.closeBtn}>
                <X size={18} color={colors.textMuted} />
              </button>
            </div>
            {progressionFailed ? (
              <LoadFailed what="this history" onRetry={() => handleSelectExercise(progressionFor)} />
            ) : progressionData === null ? (
              <div style={{ ...typography.caption, color: colors.textMuted }}>Loading…</div>
            ) : progressionData.length === 0 ? (
              <div style={{ ...typography.caption, color: colors.textMuted }}>No record history yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                {progressionData.slice().reverse().map((milestone) => (
                  <div key={milestone.created_at} style={styles.row}>
                    <div>
                      <div style={{ ...typography.body, color: colors.text, fontWeight: '600' }}>
                        {milestone.weight} kg × {milestone.reps}
                      </div>
                      <div style={{ ...typography.caption, color: colors.textSubtle }}>
                        {new Date(milestone.created_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    {typeof milestone.improvement === 'number' && (
                      <span style={{ ...typography.caption, color: colors.success, fontWeight: '700' }}>
                        ↑ {milestone.improvement.toFixed(1)} kg
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 1000,
  },
  sheet: {
    width: '100%', maxWidth: '480px', maxHeight: '85vh',
    background: colors.surface1,
    borderTopLeftRadius: radius.modal, borderTopRightRadius: radius.modal,
    padding: spacing.lg,
    overflowY: 'auto',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: spacing.lg,
  },
  closeBtn: {
    background: colors.surface2, border: 'none', borderRadius: '50%',
    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
  },
  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.surface2, borderRadius: radius.sm,
    border: `1px solid ${colors.border}`,
  },
};

export default RecordsTab;
